import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { ArrowLeft, CheckCircle2, XCircle, Clock } from "lucide-react";

export default function AttemptDetail() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);

  useEffect(() => {
    api.get(`/attempts/${attemptId}`).then((r) => setAttempt(r.data));
  }, [attemptId]);

  return (
    <AppShell title="Attempt details" subtitle="Detailed breakdown of your submission.">
      {!attempt ? (
        <p className="text-slate-500" data-testid="attempt-loading">Loading…</p>
      ) : (
        <div className="space-y-6" data-testid="attempt-detail">
          <button onClick={() => nav(-1)} className="btn-ghost text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="qs-card p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">Result</p>
                <h2 className="font-display text-2xl font-black text-slate-900 mt-1">{attempt.exam_title}</h2>
                <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> Submitted {attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleString() : "—"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-display text-4xl font-black text-slate-900">{attempt.score_percent}%</p>
                <p className="text-xs text-slate-500">{attempt.score}/{attempt.total_points} points</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {(attempt.answers || []).map((a, i) => (
              <div key={a.question_id} className="qs-card p-5" data-testid={`answer-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <p className="text-sm text-slate-700">
                    <span className="text-xs uppercase tracking-widest font-bold text-slate-500 mr-2">Q{i + 1}</span>
                    {a.type === "mcq" ? (
                      <>Selected option: <b>{a.selected_index !== null && a.selected_index !== undefined ? String.fromCharCode(65 + a.selected_index) : "—"}</b></>
                    ) : (
                      <>{a.text_answer ? <span className="italic">&ldquo;{a.text_answer}&rdquo;</span> : <span className="text-slate-400">No answer</span>}</>
                    )}
                  </p>
                  {a.type === "mcq" ? (
                    a.correct ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold"><CheckCircle2 className="w-4 h-4" /> +{a.points_awarded}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-600 text-sm font-semibold"><XCircle className="w-4 h-4" /> 0</span>
                    )
                  ) : a.pending_grading ? (
                    <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Pending manual grading</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold">
                      <CheckCircle2 className="w-4 h-4" /> {a.points_awarded ?? 0} pts
                    </span>
                  )}
                </div>
                {a.type === "descriptive" && a.feedback && (
                  <div className="mt-3 p-3 rounded-lg bg-indigo-50/60 border border-indigo-100">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-600">Teacher feedback</p>
                    <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{a.feedback}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
